import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  X,
  ChevronDown,
  ChevronUp,
  GripVertical,
  CheckCircle,
  Circle,
  HelpCircle,
  Type,
  ToggleLeft,
  AlignLeft,
  Sparkles,
  Save,
  ArrowLeft,
  Settings,
  Eye,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

const QUESTION_TYPES = [
  { value: 'multiple_choice_single', label: 'Multiple Choice (Single)', icon: CheckCircle, description: 'One correct answer' },
  { value: 'true_false', label: 'True / False', icon: ToggleLeft, description: 'Binary choice' },
  { value: 'short_answer', label: 'Short Answer', icon: Type, description: 'Text response with keywords' },
];

const DIFFICULTY_LEVELS = [
  { value: 'beginner', label: 'Beginner', color: 'bg-green-500' },
  { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-500' },
  { value: 'advanced', label: 'Advanced', color: 'bg-red-500' },
];

export default function QuizBuilder({ courseId, moduleId, quizId, token, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('questions');
  const [quizData, setQuizData] = useState({
    title: '',
    description: '',
    settings: {
      time_limit_minutes: null,
      max_attempts: 1,
      pass_percentage: 70,
      shuffle_questions: false,
      shuffle_options: false,
      show_correct_answers: true,
      show_feedback: true,
      show_score_immediately: true,
      allow_review: true,
      allow_back_navigation: true,
    },
    cpd_points: 0,
    certificate_on_pass: false,
  });
  const [questions, setQuestions] = useState([]);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  useEffect(() => {
    if (quizId) {
      fetchQuiz();
    }
  }, [quizId]);

  const fetchQuiz = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quizzes/${quizId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const quiz = res.data;
      setQuizData({
        title: quiz.title || '',
        description: quiz.description || '',
        settings: quiz.settings || quizData.settings,
        cpd_points: quiz.cpd_points || 0,
        certificate_on_pass: quiz.certificate_on_pass || false,
      });
      setQuestions(quiz.questions || []);
    } catch (error) {
      toast.error('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = (type) => {
    const newQuestion = {
      temp_id: `temp_${Date.now()}`,
      question_type: type,
      title: '',
      question_text: '',
      points: 1,
      difficulty: 'intermediate',
      explanation: '',
      options: type === 'true_false' 
        ? [
            { option_id: `opt_true_${Date.now()}`, text: 'True', is_correct: true },
            { option_id: `opt_false_${Date.now()}`, text: 'False', is_correct: false }
          ]
        : type === 'multiple_choice_single'
        ? [
            { option_id: `opt_a_${Date.now()}`, text: '', is_correct: true },
            { option_id: `opt_b_${Date.now()}`, text: '', is_correct: false },
            { option_id: `opt_c_${Date.now()}`, text: '', is_correct: false },
            { option_id: `opt_d_${Date.now()}`, text: '', is_correct: false }
          ]
        : [],
      keywords: [],
      keyword_match_threshold: 0.7,
    };
    
    setQuestions(prev => [...prev, newQuestion]);
    setExpandedQuestions(prev => ({ ...prev, [newQuestion.temp_id]: true }));
  };

  const updateQuestion = (index, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      const options = [...updated[questionIndex].options];
      
      if (field === 'is_correct' && value === true && updated[questionIndex].question_type === 'multiple_choice_single') {
        // For single choice, uncheck other options
        options.forEach((opt, i) => {
          opt.is_correct = i === optionIndex;
        });
      } else {
        options[optionIndex] = { ...options[optionIndex], [field]: value };
      }
      
      updated[questionIndex] = { ...updated[questionIndex], options };
      return updated;
    });
  };

  const addOption = (questionIndex) => {
    setQuestions(prev => {
      const updated = [...prev];
      const options = [...updated[questionIndex].options];
      options.push({
        option_id: `opt_${Date.now()}`,
        text: '',
        is_correct: false
      });
      updated[questionIndex] = { ...updated[questionIndex], options };
      return updated;
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    setQuestions(prev => {
      const updated = [...prev];
      const options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
      updated[questionIndex] = { ...updated[questionIndex], options };
      return updated;
    });
  };

  const removeQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const duplicateQuestion = (index) => {
    const question = questions[index];
    const duplicate = {
      ...JSON.parse(JSON.stringify(question)),
      temp_id: `temp_${Date.now()}`,
      question_id: undefined,
      title: `${question.title} (Copy)`,
      options: question.options?.map(opt => ({
        ...opt,
        option_id: `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }))
    };
    setQuestions(prev => [...prev.slice(0, index + 1), duplicate, ...prev.slice(index + 1)]);
    setExpandedQuestions(prev => ({ ...prev, [duplicate.temp_id]: true }));
  };

  const generateAIQuestions = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter content or topic for AI generation');
      return;
    }

    setGeneratingAI(true);
    try {
      const res = await axios.post(`${API}/questions/generate`, {
        content: aiPrompt,
        count: 5,
        difficulty: 'intermediate',
        question_types: ['multiple_choice_single', 'true_false']
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.questions && res.data.questions.length > 0) {
        const newQuestions = res.data.questions.map(q => ({
          temp_id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...q,
          options: q.options?.map((opt, i) => ({
            option_id: `opt_${Date.now()}_${i}`,
            ...opt
          }))
        }));
        setQuestions(prev => [...prev, ...newQuestions]);
        toast.success(`Generated ${newQuestions.length} questions!`);
        setAiPrompt('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || '';
      if (error.response?.status === 402 || errorMessage.toLowerCase().includes('budget')) {
        toast.error('AI budget exceeded. Please add balance to your Universal Key.', {
          duration: 6000
        });
      } else {
        toast.error('Failed to generate questions. Please try again.');
      }
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSave = async () => {
    if (!quizData.title.trim()) {
      toast.error('Please enter a quiz title');
      return;
    }

    if (questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    // Validate questions
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) {
        toast.error(`Question ${i + 1} has no question text`);
        return;
      }
      if (q.question_type === 'multiple_choice_single' || q.question_type === 'true_false') {
        if (!q.options || q.options.length < 2) {
          toast.error(`Question ${i + 1} needs at least 2 options`);
          return;
        }
        const hasCorrect = q.options.some(opt => opt.is_correct);
        if (!hasCorrect) {
          toast.error(`Question ${i + 1} has no correct answer selected`);
          return;
        }
        const hasEmptyOption = q.options.some(opt => !opt.text.trim());
        if (hasEmptyOption) {
          toast.error(`Question ${i + 1} has empty option(s)`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      // First, save all questions to the question bank
      const savedQuestionIds = [];
      
      for (const q of questions) {
        if (q.question_id) {
          // Update existing question
          await axios.put(`${API}/questions/${q.question_id}`, {
            question_type: q.question_type,
            title: q.title || q.question_text.substring(0, 50),
            question_text: q.question_text,
            points: q.points,
            difficulty: q.difficulty,
            explanation: q.explanation,
            options: q.options,
            keywords: q.keywords,
            keyword_match_threshold: q.keyword_match_threshold,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          savedQuestionIds.push(q.question_id);
        } else {
          // Create new question
          const res = await axios.post(`${API}/questions`, {
            question_type: q.question_type,
            title: q.title || q.question_text.substring(0, 50),
            question_text: q.question_text,
            points: q.points,
            difficulty: q.difficulty,
            explanation: q.explanation,
            options: q.options,
            keywords: q.keywords,
            keyword_match_threshold: q.keyword_match_threshold,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          savedQuestionIds.push(res.data.question_id);
        }
      }

      // Now save/update the quiz
      const quizPayload = {
        title: quizData.title,
        description: quizData.description,
        course_id: courseId,
        module_id: moduleId,
        settings: quizData.settings,
        question_ids: savedQuestionIds,
        cpd_points: quizData.cpd_points,
        certificate_on_pass: quizData.certificate_on_pass,
      };

      if (quizId) {
        await axios.put(`${API}/quizzes/${quizId}`, quizPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Quiz updated successfully');
      } else {
        await axios.post(`${API}/quizzes`, quizPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Quiz created successfully');
      }

      onSave();
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error('Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90">
        <div className="text-white">Loading quiz...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-navigate-dark overflow-hidden" data-testid="quiz-builder">
      {/* Header */}
      <div className="bg-navigate-surface border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-heading text-xl font-bold text-white">
                {quizId ? 'Edit Quiz' : 'Create Quiz'}
              </h1>
              <p className="text-sm text-zinc-400">
                {questions.length} question{questions.length !== 1 ? 's' : ''} • {questions.reduce((sum, q) => sum + (q.points || 1), 0)} points
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold flex items-center gap-2 disabled:opacity-50"
              data-testid="save-quiz-btn"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Quiz'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Panel - Quiz Info & Settings */}
        <div className="w-80 bg-navigate-surface border-r border-zinc-800 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="w-full border-b border-zinc-800 rounded-none bg-transparent p-0">
              <TabsTrigger value="questions" className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-navigate-gold data-[state=active]:bg-transparent">
                Questions
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-navigate-gold data-[state=active]:bg-transparent">
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="questions" className="flex-1 p-4 space-y-4 m-0">
              {/* Quiz Title */}
              <div>
                <Label>Quiz Title</Label>
                <Input
                  value={quizData.title}
                  onChange={(e) => setQuizData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Module Assessment"
                  className="bg-navigate-dark border-zinc-700"
                  data-testid="quiz-title-input"
                />
              </div>

              {/* Quiz Description */}
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={quizData.description}
                  onChange={(e) => setQuizData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the quiz..."
                  className="bg-navigate-dark border-zinc-700"
                  rows={2}
                />
              </div>

              {/* Add Question Buttons */}
              <div>
                <Label className="mb-2 block">Add Question</Label>
                <div className="grid grid-cols-1 gap-2">
                  {QUESTION_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => addQuestion(type.value)}
                      className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700 hover:border-navigate-gold hover:bg-navigate-gold/5 transition-colors text-left"
                      data-testid={`add-${type.value}-btn`}
                    >
                      <type.icon className="h-5 w-5 text-navigate-gold" />
                      <div>
                        <p className="text-sm font-medium text-white">{type.label}</p>
                        <p className="text-xs text-zinc-500">{type.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Generation */}
              <div className="pt-4 border-t border-zinc-800">
                <Label className="mb-2 block flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-navigate-gold" />
                  AI Generate Questions
                </Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Enter topic or paste content to generate questions from..."
                  className="bg-navigate-dark border-zinc-700"
                  rows={3}
                />
                <button
                  onClick={generateAIQuestions}
                  disabled={generatingAI || !aiPrompt.trim()}
                  className="w-full mt-2 px-4 py-2 bg-navigate-gold/20 border border-navigate-gold rounded-lg text-sm text-navigate-gold hover:bg-navigate-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="ai-generate-btn"
                >
                  <Sparkles className="h-4 w-4" />
                  {generatingAI ? 'Generating...' : 'Generate 5 Questions'}
                </button>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="flex-1 p-4 space-y-4 m-0">
              {/* Time Limit */}
              <div>
                <Label>Time Limit (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={quizData.settings.time_limit_minutes || ''}
                  onChange={(e) => setQuizData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, time_limit_minutes: e.target.value ? parseInt(e.target.value) : null }
                  }))}
                  placeholder="No limit"
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>

              {/* Max Attempts */}
              <div>
                <Label>Maximum Attempts</Label>
                <Input
                  type="number"
                  min="1"
                  value={quizData.settings.max_attempts}
                  onChange={(e) => setQuizData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_attempts: parseInt(e.target.value) || 1 }
                  }))}
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>

              {/* Pass Percentage */}
              <div>
                <Label>Pass Percentage (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={quizData.settings.pass_percentage}
                  onChange={(e) => setQuizData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, pass_percentage: parseInt(e.target.value) || 70 }
                  }))}
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>

              {/* CPD Points */}
              <div>
                <Label>CPD Points on Pass</Label>
                <Input
                  type="number"
                  min="0"
                  value={quizData.cpd_points}
                  onChange={(e) => setQuizData(prev => ({ ...prev, cpd_points: parseInt(e.target.value) || 0 }))}
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>

              {/* Toggle Settings */}
              <div className="space-y-3 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Shuffle Questions</Label>
                  <Switch
                    checked={quizData.settings.shuffle_questions}
                    onCheckedChange={(checked) => setQuizData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, shuffle_questions: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Shuffle Options</Label>
                  <Switch
                    checked={quizData.settings.shuffle_options}
                    onCheckedChange={(checked) => setQuizData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, shuffle_options: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Show Correct Answers</Label>
                  <Switch
                    checked={quizData.settings.show_correct_answers}
                    onCheckedChange={(checked) => setQuizData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, show_correct_answers: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Show Feedback</Label>
                  <Switch
                    checked={quizData.settings.show_feedback}
                    onCheckedChange={(checked) => setQuizData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, show_feedback: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Allow Back Navigation</Label>
                  <Switch
                    checked={quizData.settings.allow_back_navigation}
                    onCheckedChange={(checked) => setQuizData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, allow_back_navigation: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm">Certificate on Pass</Label>
                  <Switch
                    checked={quizData.certificate_on_pass}
                    onCheckedChange={(checked) => setQuizData(prev => ({ ...prev, certificate_on_pass: checked }))}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Panel - Question Editor */}
        <div className="flex-1 overflow-y-auto p-6">
          {questions.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <HelpCircle className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">No Questions Yet</h3>
                <p className="text-zinc-400 mb-6 max-w-md">
                  Start building your quiz by adding questions from the left panel, or use AI to generate questions automatically.
                </p>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.question_id || question.temp_id}
                  question={question}
                  index={index}
                  expanded={expandedQuestions[question.question_id || question.temp_id]}
                  onToggle={() => toggleQuestion(question.question_id || question.temp_id)}
                  onUpdate={(field, value) => updateQuestion(index, field, value)}
                  onUpdateOption={(optIndex, field, value) => updateOption(index, optIndex, field, value)}
                  onAddOption={() => addOption(index)}
                  onRemoveOption={(optIndex) => removeOption(index, optIndex)}
                  onRemove={() => removeQuestion(index)}
                  onDuplicate={() => duplicateQuestion(index)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Question Card Component
function QuestionCard({ 
  question, 
  index, 
  expanded, 
  onToggle, 
  onUpdate, 
  onUpdateOption,
  onAddOption,
  onRemoveOption,
  onRemove,
  onDuplicate
}) {
  const questionType = QUESTION_TYPES.find(t => t.value === question.question_type);
  const difficulty = DIFFICULTY_LEVELS.find(d => d.value === question.difficulty);

  return (
    <div 
      className={`bg-navigate-surface border rounded-xl overflow-hidden transition-all ${
        expanded ? 'border-navigate-gold/50' : 'border-zinc-800'
      }`}
      data-testid={`question-card-${index}`}
    >
      {/* Question Header */}
      <div 
        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-800/30"
        onClick={onToggle}
      >
        <GripVertical className="h-5 w-5 text-zinc-600 cursor-grab" />
        <div className="w-8 h-8 rounded-full bg-navigate-gold/20 text-navigate-gold flex items-center justify-center font-bold text-sm">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {question.question_text || 'New Question'}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-zinc-500">{questionType?.label}</span>
            <span className={`w-2 h-2 rounded-full ${difficulty?.color || 'bg-zinc-500'}`} />
            <span className="text-xs text-zinc-500">{question.points} pt{question.points !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="p-2 text-zinc-400 hover:text-white"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-2 text-zinc-400 hover:text-red-400"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-zinc-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-zinc-400" />
          )}
        </div>
      </div>

      {/* Question Editor */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-4">
          {/* Question Text */}
          <div>
            <Label>Question Text</Label>
            <Textarea
              value={question.question_text}
              onChange={(e) => onUpdate('question_text', e.target.value)}
              placeholder="Enter your question here..."
              className="bg-navigate-dark border-zinc-700"
              rows={2}
              data-testid={`question-text-${index}`}
            />
          </div>

          {/* Options for Multiple Choice / True-False */}
          {(question.question_type === 'multiple_choice_single' || question.question_type === 'true_false') && (
            <div>
              <Label className="mb-2 block">Answer Options</Label>
              <div className="space-y-2">
                {question.options?.map((option, optIndex) => (
                  <div key={option.option_id} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onUpdateOption(optIndex, 'is_correct', true)}
                      className={`p-2 rounded-lg border transition-colors ${
                        option.is_correct
                          ? 'border-green-500 bg-green-500/10 text-green-400'
                          : 'border-zinc-700 text-zinc-500 hover:border-zinc-600'
                      }`}
                      title={option.is_correct ? 'Correct answer' : 'Mark as correct'}
                    >
                      {option.is_correct ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </button>
                    <Input
                      value={option.text}
                      onChange={(e) => onUpdateOption(optIndex, 'text', e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                      className="flex-1 bg-navigate-dark border-zinc-700"
                      disabled={question.question_type === 'true_false'}
                      data-testid={`option-${index}-${optIndex}`}
                    />
                    {question.question_type !== 'true_false' && question.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => onRemoveOption(optIndex)}
                        className="p-2 text-zinc-400 hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {question.question_type !== 'true_false' && (
                <button
                  type="button"
                  onClick={onAddOption}
                  className="mt-2 text-sm text-navigate-gold hover:underline flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Option
                </button>
              )}
            </div>
          )}

          {/* Keywords for Short Answer */}
          {question.question_type === 'short_answer' && (
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Input
                value={question.keywords?.join(', ') || ''}
                onChange={(e) => onUpdate('keywords', e.target.value.split(',').map(k => k.trim()).filter(k => k))}
                placeholder="e.g., compliance, regulation, policy"
                className="bg-navigate-dark border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Answers containing these keywords will be marked correct
              </p>
            </div>
          )}

          {/* Settings Row */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-zinc-800">
            <div>
              <Label>Points</Label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={question.points}
                onChange={(e) => onUpdate('points', parseFloat(e.target.value) || 1)}
                className="bg-navigate-dark border-zinc-700"
              />
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select
                value={question.difficulty}
                onValueChange={(v) => onUpdate('difficulty', v)}
              >
                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navigate-surface border-zinc-800">
                  {DIFFICULTY_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${level.color}`} />
                        {level.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input
                value={question.title || ''}
                onChange={(e) => onUpdate('title', e.target.value)}
                placeholder="Question label"
                className="bg-navigate-dark border-zinc-700"
              />
            </div>
          </div>

          {/* Explanation */}
          <div>
            <Label>Explanation (shown after answer)</Label>
            <Textarea
              value={question.explanation || ''}
              onChange={(e) => onUpdate('explanation', e.target.value)}
              placeholder="Explain why this is the correct answer..."
              className="bg-navigate-dark border-zinc-700"
              rows={2}
            />
          </div>
        </div>
      )}
    </div>
  );
}
